import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { typographyRecipes } from "../design/recipes/typographyRecipes";
import Button from "@mui/material/Button";
import { ReactComponent as AccessibilityTriggerIcon } from "../../assets/icons/accessibility-trigger.svg";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAppLanguage } from "../hooks/useAppLanguage";
import RewampAccessibilityOptionsDialog from "./dialog/RewampAccessibilityOptionsDialog";

export const RewampAccessibilityBar = React.memo(({
  onIncreaseFontSize = () => { },
  onDecreaseFontSize = () => { },
  onResetFontSize = () => { },
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { appLanguage, handleLanguageChange } = useAppLanguage();
  const [dialogOpen, setDialogOpen] = useState(false);
  // Trigger doubles as the popover anchor so the panel works for click and Ctrl+F2.
  const triggerRef = useRef(null);

  // Keyboard shortcut for Ctrl+F2
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === "F2") {
        e.preventDefault();
        setDialogOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <>
      <Box
        sx={{
          bgcolor: "primary.dark",
          // Header decorative border per Figma — Butterscotch (Wild Lime reserved for Open status badge per styleGuide)
          borderBottom: "1.6px solid",
          borderColor: "accent.butterscotch",
          px: { xs: 2, md: 4, lg: 9 },
          py: 0.75,
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "flex-end", md: "space-between" },
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: { xs: "none", md: "flex" }, gap: 3, alignItems: "center" }}>
          <Typography
            component="a"
            href="#main"
            sx={typographyRecipes.a11yHeaderLink}
            onClick={(e) => {
              e.preventDefault();
              // Prefer a page-specific skip target; fall back to the <main id="main-portal"> landmark.
              const el =
                document.querySelector("[data-skip-target]") ||
                document.getElementById("main-portal");
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                el.focus({ preventScroll: true });
              }
            }}
          >
            {t("Skip_to_main_content")}
          </Typography>
          <Typography
            component="a"
            href="screenreaderaccess"
            sx={typographyRecipes.a11yHeaderLink}
            onClick={(e) => {
              e.preventDefault();
              navigate(`${window.contextPath}/screenreaderaccess`);
            }}
          >
            {t("ScreenReader")}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Button
            onClick={() => handleLanguageChange("en_IN")}
            variant={appLanguage === "en_IN" ? "a11yLangActive" : "a11yLang"}
            aria-pressed={appLanguage === "en_IN"}
            aria-label={`EN — ${t("Switch to English")}`}
          >
            EN
          </Button>
          <Button
            onClick={() => handleLanguageChange("hi_IN")}
            variant={appLanguage === "hi_IN" ? "a11yLangActive" : "a11yLang"}
            aria-pressed={appLanguage === "hi_IN"}
            aria-label={`\u0939\u093F — ${t("Switch to Hindi")}`}
          >
            हि
          </Button>

          <Box
            sx={{
              width: "1px",
              height: "16px",
              bgcolor: "rgba(255, 255, 255, 0.15)",
              mx: "1px",
            }}
            aria-hidden="true"
          />

          <Button
            ref={triggerRef}
            onClick={() => setDialogOpen(true)}
            aria-label={t("Open Accessibility Options")}
            aria-keyshortcuts="Ctrl+F2"
            aria-haspopup="dialog"
            aria-expanded={dialogOpen}
            sx={{ minWidth: "33.6px" }}
            variant="a11yTrigger"
          >
            <AccessibilityTriggerIcon style={{ fontSize: "16px", width: "16px", height: "16px" }} />
          </Button>
        </Box>
      </Box>

      <RewampAccessibilityOptionsDialog
        open={dialogOpen}
        anchorEl={triggerRef.current}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
});

export default RewampAccessibilityBar;
